// "Use my theme's colours" for every widget (Product Bundle, Cross-sell, Upsell, Free Gift,
// plus the older cart / product / inventory / sales badge / shipping / progress bar /
// BOGO modal / BOGO FAB / countdown widgets). The storefront derives the widget palette from the theme at
// runtime when the widget settings say so; a merchant who picked their own colours
// keeps them. This module is the ONE place that holds the contract shared with the
// storefront (theme-app-extension widgets) and Prime-Backend:
//
//   Theme mode is ON when `UseThemeColors === true`, OR when `UseThemeColors` is
//   undefined (saved before the flag existed) AND every colour key present in the
//   saved settings equals the OLD shipped default for that widget (hex compared
//   case-insensitively). A missing or empty colour key counts as "not customised".
//
// The OLD shipped defaults below are the colour values Prime-Backend's
// shop.service.ts activateDefaultWidgets() provisioned on install (bundle, cross-sell,
// upsell and gift blocks). They are frozen on purpose: even if the current defaults
// in config/defaultWidgetSettings.js or adaptors/shop/shop-info.js change later, the
// detection must keep recognising what older shops actually have stored.

export const OLD_DEFAULT_WIDGET_COLORS = {
  bundle: {
    WidgetBackgroundColor: '#F7F7F7',
    HeaderBackgroundColor: '#CDFEE1',
    TitleColor: '#303030',
    SubTitleColor: '#616161',
    TextColor: '#303030',
    GiftBorderColor: '#A8D8FF',
    GiftBackgroundColor: '#EAF4FF',
    ProgressBarColor: '#45946D',
    ProgressBackgroundColor: '#D9F0E4',
    ButtonBackgroundColor: '#303030',
    ButtonTextColor: '#FFFFFF',
  },
  crossSell: {
    ButtonBackgroundColor: '#29845A',
    ButtonTextColor: '#ffffff',
    WidgetBackgroundColor: '#F7F7F7',
    TextColor: '#303030',
  },
  upsell: {
    ButtonBackgroundColor: '#29845A',
    ButtonTextColor: '#ffffff',
    WidgetBackgroundColor: '#F7F7F7',
    TextColor: '#303030',
  },
  gift: {
    WidgetBackgroundColor: '#FFFFFF',
    TextColor: '#222222',
    TitleColor: '#111111',
    ProgressBarColor: '#111111',
    ProgressBackgroundColor: '#E6E6E6',
    ButtonBackgroundColor: '#111111',
    ButtonTextColor: '#FFFFFF',
  },
  // The older widgets: colours below are the values Prime-Backend provisioned on install
  // (config/defaultWidgetSettings.js at the time the flag was introduced). The cart box
  // now ships neutral gray defaults, but shops saved before that still hold these greens.
  cart: {
    totalColor: '#000000',
    savedColor: '#45946D',
    savedBgColor: '#D9F0E4',
    boarderinSavedColor: '#45946D',
  },
  product: {
    titleColor: '#000000',
    tableBgColor: '#FFFFFF',
    tableTextColor: '#000000',
    borderColor: '#D0D0D0',
  },
  inventory: {
    TextColor: '#8E1F0B',
    BackgroundColor: '#FEDAD9',
  },
  salesBadge: {
    salesBadgeTextColor: '#FFFFFF',
    salesBadgeBackgroundColor: '#FF0000',
    badgeColor: '#FFFFFF',
    badgeBackgroundColor: '#FF0000',
  },
  shipping: {
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    progressColor: '#19A07F',
    progressBackground: '#D9D9D9',
    cardBorderColor: '#000000',
  },
  progressBar: {
    textColor: '#000000',
    cardBg: '#FFFFFF',
    cardBorder: '#000000',
    trackColor: '#D9D9D9',
    fgColor: '#19A07F',
  },
  bogoModal: {
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
    borderColor: '#C0C0C0',
    popupBackgroundColor: '#F4F4F4',
    headerBackgroundColor: '#616161',
    headerTextColor: '#FFFFFF',
    saleBadgeColor: '#3752B2',
  },
  bogoFab: {
    fabBackgroundColor: '#03A87C',
    fabIconColor: '#FFFFFF',
    closeButtonColor: '#FFFFFF',
    closeButtonIconColor: '#C0C0C0',
  },
  countdown: {
    textColor: '#333333',
    digitColor: '#333333',
    digitBackgroundColor: '#f8f4f1',
    progressBarColor: '#45946D',
    progressBarBorderColor: '#808080',
  },
};

// Neutral palette the admin preview renders with while theme mode is on (the admin
// cannot know the merchant's theme; the storefront resolves the real colours).
export const THEME_PREVIEW_COLORS = {
  card: '#FFFFFF',
  text: '#1A1A1A',
  muted: '#616161',
  accent: '#111111',
  buttonText: '#FFFFFF',
  track: '#E6E6E6',
  border: 'rgba(0,0,0,.12)',
};

const P = THEME_PREVIEW_COLORS;

// The neutral palette mapped onto each widget's colour keys, so a preview can be fed
// `{ ...settings, ...THEME_PREVIEW_PALETTE[widget] }` without changing its own code.
export const THEME_PREVIEW_PALETTE = {
  bundle: {
    WidgetBackgroundColor: P.card,
    HeaderBackgroundColor: P.card,
    TitleColor: P.text,
    SubTitleColor: P.muted,
    TextColor: P.text,
    GiftBorderColor: P.border,
    GiftBackgroundColor: P.card,
    ProgressBarColor: P.accent,
    ProgressBackgroundColor: P.track,
    ButtonBackgroundColor: P.accent,
    ButtonTextColor: P.buttonText,
  },
  crossSell: {
    WidgetBackgroundColor: P.card,
    TextColor: P.text,
    ButtonBackgroundColor: P.accent,
    ButtonTextColor: P.buttonText,
  },
  upsell: {
    WidgetBackgroundColor: P.card,
    TextColor: P.text,
    ButtonBackgroundColor: P.accent,
    ButtonTextColor: P.buttonText,
  },
  gift: {
    WidgetBackgroundColor: P.card,
    TextColor: P.text,
    TitleColor: P.text,
    ProgressBarColor: P.accent,
    ProgressBackgroundColor: P.track,
    ButtonBackgroundColor: P.accent,
    ButtonTextColor: P.buttonText,
  },
  cart: {
    totalColor: P.text,
    savedColor: P.text,
    savedBgColor: P.card,
    boarderinSavedColor: P.border,
  },
  product: {
    titleColor: P.text,
    tableBgColor: P.card,
    tableTextColor: P.text,
    borderColor: P.border,
  },
  inventory: {
    TextColor: P.text,
    BackgroundColor: P.card,
  },
  salesBadge: {
    salesBadgeTextColor: P.buttonText,
    salesBadgeBackgroundColor: P.accent,
    badgeColor: P.buttonText,
    badgeBackgroundColor: P.accent,
  },
  shipping: {
    backgroundColor: P.card,
    textColor: P.text,
    progressColor: P.accent,
    progressBackground: P.track,
    cardBorderColor: P.border,
  },
  progressBar: {
    textColor: P.text,
    cardBg: P.card,
    cardBorder: P.border,
    trackColor: P.track,
    fgColor: P.accent,
  },
  bogoModal: {
    primaryColor: P.accent,
    secondaryColor: P.card,
    borderColor: P.border,
    popupBackgroundColor: P.card,
    headerBackgroundColor: P.accent,
    headerTextColor: P.buttonText,
    saleBadgeColor: P.accent,
  },
  bogoFab: {
    fabBackgroundColor: P.accent,
    fabIconColor: P.buttonText,
    closeButtonColor: P.buttonText,
    closeButtonIconColor: P.muted,
  },
  countdown: {
    textColor: P.text,
    digitColor: P.text,
    digitBackgroundColor: P.track,
    progressBarColor: P.accent,
    progressBarBorderColor: P.border,
  },
};

const normalizeColor = (value) => String(value).trim().toLowerCase();

/**
 * True when every colour key of `widgetKey` that is present in `settings` still holds
 * the OLD shipped default (case-insensitive). Missing / empty keys do not count as
 * customised, so an object with no colour keys at all is "all defaults".
 */
export function isOldDefaultPalette(widgetKey, settings) {
  const table = OLD_DEFAULT_WIDGET_COLORS[widgetKey];
  if (!table) return false;
  if (!settings) return true;
  return Object.keys(table).every((key) => {
    const value = settings[key];
    if (value === undefined || value === null || value === '') return true;
    return normalizeColor(value) === normalizeColor(table[key]);
  });
}

/**
 * The theme-mode state the storefront will use for these settings: the explicit flag
 * when saved, else the old-default detection (older shop).
 */
export function resolveUseThemeColors(widgetKey, settings) {
  if (typeof settings?.UseThemeColors === 'boolean') return settings.UseThemeColors;
  return isOldDefaultPalette(widgetKey, settings);
}

/** `settings` with an explicit `UseThemeColors`, so the next save persists the detected state. */
export function withUseThemeColors(widgetKey, settings) {
  return { ...settings, UseThemeColors: resolveUseThemeColors(widgetKey, settings) };
}

/** Settings to feed the admin preview: the neutral palette while theme mode is on, else as saved. */
export function previewColorSettings(widgetKey, settings) {
  if (!settings || !settings.UseThemeColors) return settings;
  return { ...settings, ...THEME_PREVIEW_PALETTE[widgetKey] };
}
